/* eslint-disable camelcase,@typescript-eslint/no-non-null-assertion */

import * as _ from "lodash";
import { Column, Entity, OneToOne, PrimaryGeneratedColumn } from "typeorm";
import { Vehicle } from "./reg_vehicles.entity";
import { CanNormalizedState } from "../interfaces/can-normalized.interface";
import { FuelDetectionStateV2 } from "../interfaces/fuel-detection.interface";
import { FuelStopRulesState } from "../interfaces/fuel-stop-rules.interface";

@Entity("reg_vehicle_current_infos")
export class VehicleCurrentInfo {

  @PrimaryGeneratedColumn("uuid")
    id_vehicle_current_info?: string;

  @Column({ type: "boolean", nullable: true })
    can_engine?: boolean;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_odometer?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_total_fuel_used?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_total_fuel_used_counted?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_speed?: number;

  @Column({ type: "numeric", nullable: true })
    can_engine_rpm?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_engine_coolant_temperature?: number;

  @Column({ type: "varchar", nullable: true })
    can_fuel_consumption?: string;

  @Column({ type: "varchar", nullable: true })
    can_fuel_level?: string;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_total_engine_hours?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_total_engine_hours_counted?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_total_driving_time?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_total_idle_fuel_used?: number;

  @Column({ type: "numeric", nullable: true })
    can_gross_combination_vehicle_weight?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        // NULL must come back as null — parseFloat(null) is NaN, and NaN would be written back to
        // the column by the whole-entity update in Backend-Socket (PG numeric accepts 'NaN').
        return value == null ? null : parseFloat(value);
      }
    }
  })
    fuel_tank_size?: number;

  // The tank capacity LEARNED by the fuel detector's calibration — never typed by a user, never
  // overwriting fuel_tank_size. Estimated ONCE (while NULL) and only re-estimated after a user
  // resets it. The capacity in force everywhere is fuel_tank_size ?? fuel_tank_size_estimated.
  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        // NULL must come back as null — parseFloat(null) is NaN, and NaN would be written back to
        // the column by the whole-entity update in Backend-Socket (PG numeric accepts 'NaN').
        return value == null ? null : parseFloat(value);
      }
    }
  })
    fuel_tank_size_estimated?: number;

  @Column({ type: "timestamp", nullable: true })
    fuel_tank_size_estimated_at?: Date;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    max_temperature_limit?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    harsh_acceleration_threshold?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    harsh_braking_threshold?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    harsh_cornering_threshold?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    max_rpm_limit?: number;

  @Column({
    type: "numeric",
    nullable: true,
    transformer: {
      to(value) {
        return value;
      },
      from(value) {
        return parseFloat(value);
      }
    }
  })
    can_idle_engine_hours_counted?: number;

  @Column({ type: "jsonb", nullable: true })
    can_normalized_state?: CanNormalizedState | null;

  /**
   * The fuel rules' rolling state. Version 3 is the STOP-ORIENTED set (BCF-263); version 2 is the
   * calm-ledger detector it replaced, kept in the type only because rows still carry it until each
   * vehicle is read again — they share nothing and a v2 blob is dropped, not migrated.
   */
  @Column({ type: "jsonb", nullable: true })
    fuel_detection_state?: FuelStopRulesState | FuelDetectionStateV2 | null;

  @OneToOne(() => Vehicle, (vehicle) => vehicle.current_info, { nullable: true })
    vehicle?: Vehicle;

  GetFuelUsed?() : number | null {
    if (_.isFinite(this.can_total_fuel_used)) return this.can_total_fuel_used!;

    if (_.isFinite(this.can_total_fuel_used_counted)) return this.can_total_fuel_used_counted!;

    return null;
  }

  GetIdleFuelUsed?() : number | null {
    return _.isFinite(this.can_total_idle_fuel_used) ? this.can_total_idle_fuel_used! : null;
  }

}
